import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'

/** How long a finger rests on a row before the row is picked. */
const LONG_PRESS_MS = 500
/** A finger that moves further than this is scrolling, not pressing. */
const PRESS_SLOP_PX = 10

/** Keys never taken from these: typing, menus, and dialogs opened from the list. */
const NOT_THE_LIST =
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], input, textarea, select, [contenteditable="true"]'

/**
 * Rows picked on a list, as Linear picks them: by checkbox, shift-click for a
 * range, x for the row in focus, Ctrl or Cmd+A for every row shown, Esc to let
 * go. The selection is kept by id, so it outlasts a new page, view or search
 * on the same list; the rows are remembered as last seen.
 *
 * On a phone, rows are picked in selection mode, entered by a Select button or
 * a long press on a row; a tap then picks the row instead of opening it.
 */
export function useRowSelection<T extends { id: string }>() {
  const [selected, setSelected] = useState<ReadonlyMap<string, T>>(() => new Map())
  /** Where a shift-click range starts: the row last picked by hand. */
  const anchor = useRef<string | null>(null)
  const [selecting, setSelecting] = useState(false)
  const press = useRef<{ timer: number; x: number; y: number } | null>(null)
  /** A long press just picked a row: the click that ends it must not open the row. */
  const pressed = useRef(false)

  function toggle(row: T, visible: readonly T[], range = false) {
    const from = range && anchor.current ? visible.findIndex((r) => r.id === anchor.current) : -1
    const to = visible.findIndex((r) => r.id === row.id)
    setSelected((current) => {
      const next = new Map(current)
      if (from >= 0 && to >= 0) {
        // A range takes the state of the row it ends on, as a checklist does.
        const on = !current.has(row.id)
        for (const r of visible.slice(Math.min(from, to), Math.max(from, to) + 1)) {
          if (on) next.set(r.id, r)
          else next.delete(r.id)
        }
      } else if (next.has(row.id)) {
        next.delete(row.id)
      } else {
        next.set(row.id, row)
      }
      return next
    })
    anchor.current = row.id
  }

  function setAll(visible: readonly T[], on: boolean) {
    setSelected((current) => {
      const next = new Map(current)
      for (const row of visible) {
        if (on) next.set(row.id, row)
        else next.delete(row.id)
      }
      return next
    })
  }

  function clear() {
    setSelected(new Map())
    setSelecting(false)
    anchor.current = null
  }

  /** After a batch: only these stay selected, as they are now. */
  function keep(ids: readonly string[], visible: readonly T[]) {
    setSelected((current) => {
      const next = new Map<string, T>()
      for (const id of ids) {
        const row = visible.find((r) => r.id === id) ?? current.get(id)
        if (row) next.set(id, row)
      }
      return next
    })
  }

  /** The list's keys; `x` acts on the row holding focus. */
  function onKeyDown(event: KeyboardEvent, visible: readonly T[]) {
    const target = event.target instanceof Element ? event.target : null
    if (target?.closest(NOT_THE_LIST)) return
    if (event.key === 'Escape' && selected.size) {
      event.preventDefault()
      clear()
    } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
      if (!visible.length) return
      event.preventDefault()
      setAll(visible, true)
    } else if (
      event.key.toLowerCase() === 'x' &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      const id = target?.closest<HTMLElement>('[data-row-id]')?.dataset.rowId
      const row = visible.find((r) => r.id === id)
      if (row) {
        event.preventDefault()
        toggle(row, visible, event.shiftKey)
      }
    }
  }

  function cancelPress() {
    if (press.current) window.clearTimeout(press.current.timer)
    press.current = null
  }

  /** What a selectable row carries: its id for the keys, its state, and the touch gestures. */
  function rowProps(row: T, visible: readonly T[]) {
    return {
      'data-row-id': row.id,
      'data-state': selected.has(row.id) ? 'selected' : undefined,
      // Focusable by a click, so `x` knows the row; not a tab stop.
      tabIndex: -1,
      // No link preview on iOS while a finger rests on the row.
      style: { WebkitTouchCallout: 'none' as const },
      onPointerDown: (event: ReactPointerEvent) => {
        if (event.pointerType !== 'touch' || selecting) return
        // A finger resting in a dialog opened from the row (quick edit) is not on the row.
        if (!event.currentTarget.contains(event.target as Node)) return
        cancelPress()
        press.current = {
          x: event.clientX,
          y: event.clientY,
          timer: window.setTimeout(() => {
            press.current = null
            pressed.current = true
            setSelecting(true)
            toggle(row, visible)
          }, LONG_PRESS_MS),
        }
      },
      onPointerMove: (event: ReactPointerEvent) => {
        const start = press.current
        if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > PRESS_SLOP_PX) {
          cancelPress()
        }
      },
      onPointerUp: cancelPress,
      onPointerCancel: cancelPress,
      onContextMenu: (event: ReactMouseEvent) => {
        if (pressed.current || selecting) event.preventDefault()
      },
      onClickCapture: (event: ReactMouseEvent) => {
        if (pressed.current) {
          pressed.current = false
          event.preventDefault()
          event.stopPropagation()
        } else if (selecting) {
          // In selection mode a tap picks the row; nothing in it opens.
          event.preventDefault()
          event.stopPropagation()
          toggle(row, visible, event.shiftKey)
        }
      },
    }
  }

  return {
    selected,
    rows: [...selected.values()],
    count: selected.size,
    isSelected: (id: string) => selected.has(id),
    toggle,
    setAll,
    clear,
    keep,
    onKeyDown,
    rowProps,
    selecting,
    setSelecting,
  }
}

export type RowSelection<T extends { id: string }> = ReturnType<typeof useRowSelection<T>>

/**
 * The selection's keys, anywhere on the page but a field, menu or dialog.
 * Off for anyone who cannot change the rows.
 */
export function useSelectionKeys<T extends { id: string }>(
  selection: RowSelection<T>,
  visible: readonly T[],
  enabled: boolean,
) {
  const latest = useRef({ selection, visible })
  useEffect(() => {
    latest.current = { selection, visible }
  })
  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (event: KeyboardEvent) =>
      latest.current.selection.onKeyDown(event, latest.current.visible)
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}
